import {useState, forwardRef, memo} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import ImageIcon from '@mui/icons-material/ImageOutlined'


const placeholderStyles = {
  root: css`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f0f0f0;
    padding: 30%;
  `,
  icon: css`
    height: 100%;
    width: 100%;
    color: #979797;
    max-width: 80px;
  `,
}

// Returns the aspect ratio box styles
const cssStyles = ({ aspect, paddingTop, borderRadius }) => ({
  aspectRatioBox: css`
    width: 100%;
    border-radius: ${borderRadius || '7px'};
    position: relative;
    overflow: hidden;

    /* Modern browsers: use native aspect-ratio */
    @supports (aspect-ratio: 1/1) {
      aspect-ratio: ${aspect.replace(':', '/')};
      /* no height/padding needed */
    }

    /* Fallback for older browsers */
    @supports not (aspect-ratio: 1/1) {
      height: 0;
      padding-top: ${paddingTop};
    }
  `,
  aspectRatioBoxInside: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `,
})


const DefaultPlaceholder = (props) => (
  <div css={placeholderStyles.root} {...props}>
    <ImageIcon css={placeholderStyles.icon} />
  </div>
)


const AspectImage = forwardRef(({
  src,
  alt,
  placeholder,
  loading = 'auto',
  aspect = '1:1',
  borderRadius,
  onLoad,
  onError,
  ...props
}, ref) => {
    const [error, setError] = useState(false)
    const [loaded, setLoaded] = useState(false)

    // Calculate paddingTop based on aspect ratio (e.g., "16:9" => "56.25%")
    const [denom, numer] = aspect.split(':')
    const paddingTop = (parseInt(numer, 10) / parseInt(denom, 10)) * 100 + '%'
    const styles = cssStyles({ aspect, paddingTop, borderRadius })


    const handleLoad = (e) => {
      setLoaded(true)
      if (onLoad) {
        onLoad(e)
      }
    }

    const handleError = (e) => {
      setError(true)
      if (onError) {
        onError(e)
      }
    }


    return (
      <div css={styles.aspectRatioBox}>
        <div css={styles.aspectRatioBoxInside}>
          {(!src || error) ? (
            placeholder ?? <DefaultPlaceholder {...props} />
          ) : (
            <img
              src={src}
              alt={alt}
              ref={ref}
              loading={loading}
              onLoad={handleLoad}
              onError={handleError}
              css={css`
                width: 100%;
                height: 100%;
                object-fit: cover;
                opacity: ${loaded ? 1 : 0};
                transition: opacity 0.3s ease-in;
              `}
              {...props}
            />
          )}
        </div>
      </div>
    )
  }
)

export default memo(AspectImage)


